// Copyright (c) Docugami, Inc. All rights reserved.

import { DefaultButton, Dropdown, IButtonStyles, IconButton, IIconProps, Modal, PrimaryButton, TextField, Toggle, Pivot, PivotItem, Checkbox } from "@fluentui/react";
import { dgFontFamily, userFontFamily } from "components/common/Fonts";
import { greyText, homeGray, primaryTheme } from "components/shell/presentation/AppStyles";
import { useWaitCursor } from "hooks/WaitCursor";
import { useEffect, useState } from "react";
import { useGetLabelQualityControlSettingsQuery, useUpdateLabelQualityControlSettingsMutation, useDeleteLabelQualityControlUserDescriptionMutation, DataType } from "store/frontend/frontendSlice";
import styled from "styled-components";
import { QualityControlCancelledSchema, QualityControlDescriptionConfirmedSchema, QualityControlDescriptionRevertedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";


interface LabelQualityControlDialogProps {
    docsetId: string;
    labelId: string;
    onClose: () => void;
}

const StyledTextField = styled(TextField)`
    .ms-TextField-fieldGroup:after {
        border-color: ${homeGray};
    }
`
const ModalTitle = styled.div`
    font-family: ${dgFontFamily};
    font-weight: 600;
    font-size: 18px;
    padding-left: 10px;
`

const ModalHeader = styled.div`
    display: flex;
    flex-direction: row;
    flex: '1 1 auto';
    align-items: center;
`

const ModalContent = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const ModalBody = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0px 12px;
`

const ModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    padding: 0px 12px 12px 12px;
`

const Spacer = styled.div`
    flex-grow: 1;
`

const HelpText = styled.span`
    font-family: ${dgFontFamily};
    font-size: 12px;
    font-weight: 500;
`

const iconButtonStyles: Partial<IButtonStyles> = {
    root: {
        color: primaryTheme.palette.neutralPrimary,
        marginLeft: 'auto',
        marginRight: '2px',
        alignItems: 'center',
    },
    rootHovered: {
        color: primaryTheme.palette.neutralDark,
    },
};

const GeneralSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 8px;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;
`;

const Label = styled.div`
    font-family: ${dgFontFamily};
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 4px;
`;

const CheckboxRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 22px;
`;

const CheckboxLabel = styled.span`
    font-family: ${dgFontFamily};
    font-weight: 500;
    font-size: 14px;
    margin-left: 6px;
`;

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const StyledModalBody = styled(ModalBody)`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
`;

const cancelIcon: IIconProps = { iconName: 'Cancel' };


export const LabelQualityControlDialog = (props: LabelQualityControlDialogProps): JSX.Element => {
    const { data: settings, isLoading: queryLoading } = useGetLabelQualityControlSettingsQuery({ docsetId: props.docsetId, labelId: props.labelId });
    const [triggerUpdate, updateStatus] = useUpdateLabelQualityControlSettingsMutation();
    const [triggerDelete, deleteStatus] = useDeleteLabelQualityControlUserDescriptionMutation();

    const [description, setDescription] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [isNullable, setIsNullable] = useState(false);
    const [dataType, setDataType] = useState(DataType.General);
    const [selectedTab, setSelectedTab] = useState<string>("general");

    useWaitCursor(updateStatus.isLoading || deleteStatus.isLoading || queryLoading);

    useEffect(() => {
        setDescription(settings?.description || '');
        setEnabled(!!settings?.enabled);
        setIsNullable(!!settings?.isNullable);
        setDataType(settings?.dataType || DataType.General);
    }, [settings]);

    function dataTypeOptions() {
        return Object.values(DataType).filter(x => x !== DataType.ComplexType).map(dataType => ({
            key: dataType,
            text: dataType
        }));
    }

    async function commit() {
        Telemetry.trackEvent<QualityControlDescriptionConfirmedSchema>({
            docSetId: props.docsetId,
            labelId: props.labelId,
            eventName: TelemetryEvent.QualityControlDescriptionConfirmed
        });

        if (valuesChanged()) {
            await triggerUpdate({
                labelId: props.labelId,
                docsetId: props.docsetId,
                description: description,
                dataType: dataType,
                isNullable: isNullable,
                enabled: !!enabled
            })
        }

        props.onClose();
    }

    function dismiss() {
        Telemetry.trackEvent<QualityControlCancelledSchema>({
            docSetId: props.docsetId,
            labelId: props.labelId,
            eventName: TelemetryEvent.QualityControlCancelled
        });
        props.onClose();
    }

    async function revert() {
        Telemetry.trackEvent<QualityControlDescriptionRevertedSchema>({
            docSetId: props.docsetId,
            labelId: props.labelId,
            eventName: TelemetryEvent.QualityControlDescriptionReverted
        });
        await triggerDelete({ docsetId: props.docsetId, labelId: props.labelId });
        props.onClose();
    }

    function validateInput(allowEmpty: boolean): boolean {
        const trimmed = description.trim();
        return trimmed.length <= 2000 && (allowEmpty || trimmed.length > 0);
    }

    function valuesChanged(): boolean {
        const enabledChanged = enabled !== !!settings?.enabled;
        const isNullableChanged = settings?.isNullable === null ? isNullable !== false : isNullable !== !!settings?.isNullable;
        const dataTypeChanged = settings?.dataType === null ? dataType !== DataType.General : dataType !== settings?.dataType;
        const descriptionChanged = description.trim() !== settings?.description?.trim();

        return enabledChanged ||
            isNullableChanged ||
            dataTypeChanged ||
            descriptionChanged;
    }

    function isConfirmButtonDisabled(): boolean {
        if (updateStatus.isLoading || deleteStatus.isLoading) {
            return true;
        }

        const valuesHaveChanged = valuesChanged();
        const allowEmptyDescription = valuesHaveChanged && !settings?.description?.trim()
        return !(validateInput(allowEmptyDescription) && valuesHaveChanged);
    }

    function dialog(): JSX.Element {
        if (queryLoading) {
            return <div />
        }

        return <>
            <Modal
                isOpen={true}
                onDismiss={dismiss}
                isBlocking={true}
                styles={{
                    root: { overflow: 'hidden' },
                    main: {
                        overflow: 'hidden',
                        borderRadius: '5px',
                        background: primaryTheme.palette.neutralLighterAlt,
                        padding: '0px 0px 0px 0px',
                        minHeight: '100px',
                        height: 350,
                        maxHeight: 350,
                        display: 'flex',
                        flexDirection: 'column'
                    },
                    scrollableContent: {
                        overflow: 'hidden',
                        maxHeight: '75vh',
                        maxWidth: '50vw',
                        minWidth: '800px',
                        height: '100%'
                    }
                }}>
                <ModalContent style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <ModalHeader>
                        <ModalTitle>Label Settings</ModalTitle>
                        <IconButton
                            styles={iconButtonStyles}
                            iconProps={cancelIcon}
                            ariaLabel="Close popup modal"
                            onClick={dismiss} />
                    </ModalHeader>
                    <FlexColumn>
                        <StyledModalBody>
                            <Pivot
                                selectedKey={selectedTab}
                                onLinkClick={item => setSelectedTab(item?.props.itemKey || "general")}
                                styles={{ root: { marginBottom: 12 } }}
                            >
                                <PivotItem headerText="General" itemKey="general">
                                    <GeneralSection>
                                        <Row>
                                            <div>
                                                <Label>Data type</Label>
                                                <Dropdown
                                                    label={undefined}
                                                    options={dataTypeOptions()}
                                                    selectedKey={dataType}
                                                    onChange={(ev, option) => setDataType(option?.key as DataType || DataType.General)}
                                                    styles={{ dropdown: { width: 180 } }}
                                                />
                                            </div>
                                            <CheckboxRow>
                                                <Checkbox
                                                    checked={isNullable}
                                                    onChange={(_, checked) => setIsNullable(!!checked)}
                                                />
                                                <CheckboxLabel>
                                                    Allow Empty Values
                                                </CheckboxLabel>
                                            </CheckboxRow>
                                        </Row>
                                        <div>
                                            <Label>Description</Label>
                                            <StyledTextField
                                                disabled={updateStatus.isLoading || deleteStatus.isLoading}
                                                value={description as string}
                                                placeholder={!!!settings?.description ? "A description has not yet been generated.  It will be generated after more instances have been confirmed." : "Enter a descriptions, or use the Revert button to use the system generated description."}
                                                onChange={(e, t) => setDescription(t as string)}
                                                multiline={true}
                                                rows={4}
                                                styles={{
                                                    fieldGroup: {
                                                        borderColor: greyText,
                                                        fontFamily: userFontFamily,
                                                    }
                                                }}
                                            />
                                        </div>
                                    </GeneralSection>
                                </PivotItem>
                                <PivotItem headerText="Quality Control" itemKey="quality">
                                    <HelpText>
                                        All proposed answers will be checked for data type and empty value compliance.  Desciption based checking can additionally be applied when enabled:
                                    </HelpText>
                                    <Toggle
                                        key="EnabledToggle"
                                        checked={enabled}
                                        label=""
                                        onText="Enabled"
                                        offText="Disabled"
                                        onChange={(ev, value) => setEnabled(!!value)}
                                        styles={{ root: { marginTop: 18 } }}
                                    />
                                </PivotItem>
                            </Pivot>
                        </StyledModalBody>
                        <ModalFooter>
                            {!!settings?.isUser && <DefaultButton
                                onClick={revert}
                                text='Revert'
                                disabled={updateStatus.isLoading || deleteStatus.isLoading}
                                styles={{
                                    root: {
                                        color: greyText,
                                        border: 'none'
                                    }
                                }} />}
                            <Spacer />
                            <DefaultButton
                                onClick={dismiss}
                                text='Close'
                                disabled={updateStatus.isLoading || deleteStatus.isLoading}
                                styles={{
                                    root: {
                                        color: greyText,
                                        border: 'none'
                                    }
                                }} />
                            <PrimaryButton
                                onClick={commit}
                                text='Update'
                                disabled={isConfirmButtonDisabled()} />
                        </ModalFooter>
                    </FlexColumn>
                </ModalContent>
            </Modal>
        </>
    }

    return dialog()
}
